import { useCallback, useState } from 'react';
import { parkingQueueApi } from '../../api/parkingQueueApi';
import { ParkingQueueEntry, ParkingQueueEntryInput, QueueCloseReason, QueueLeaseLinkInput } from '../../types/parkingQueue';

export interface ParkingQueueMutations {
    submitting: boolean;
    error: string | null;
    clearError: () => void;
    createEntry: (input: ParkingQueueEntryInput) => Promise<ParkingQueueEntry>;
    updateEntry: (entryKey: string, input: ParkingQueueEntryInput) => Promise<void>;
    closeEntry: (entryKey: string, reason: QueueCloseReason, note: string | undefined) => Promise<void>;
    reopenEntry: (entryKey: string, note?: string) => Promise<void>;
    addNote: (entryKey: string, note: string) => Promise<void>;
    /** Links a lease written for the entry and closes the entry as accepted. */
    linkLease: (entryKey: string, input: QueueLeaseLinkInput) => Promise<void>;
}

/** Every queue mutation with shared submitting/error state; `onMutated` reloads the caller after each success. */
export const useParkingQueueMutations = (onMutated: () => Promise<void>): ParkingQueueMutations => {
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const run = useCallback(async <T,>(fallback: string, action: () => Promise<T>): Promise<T> => {
        setSubmitting(true);
        setError(null);
        try {
            const result = await action();
            await onMutated();
            return result;
        } catch (e) {
            const message = e instanceof Error ? e.message : fallback;
            setError(message);
            throw e;
        } finally {
            setSubmitting(false);
        }
    }, [onMutated]);

    return {
        submitting,
        error,
        clearError: () => setError(null),
        createEntry: input => run('Kunde inte skapa köplatsen', () => parkingQueueApi.create(input)),
        updateEntry: (key, input) => run('Kunde inte spara köplatsen', () => parkingQueueApi.update(key, input)),
        closeEntry: (key, reason, note) => run('Kunde inte avsluta köplatsen', () => parkingQueueApi.close(key, reason, note)),
        reopenEntry: (key, note) => run('Kunde inte återöppna köplatsen', () => parkingQueueApi.reopen(key, note)),
        addNote: (key, note) => run('Kunde inte spara anteckningen', () => parkingQueueApi.addNote(key, note)),
        linkLease: (key, input) => run('Kunde inte koppla hyresavtalet', () => parkingQueueApi.linkLease(key, input)),
    };
};
